import React, { useContext, useEffect, useState } from "react";
import { Card, CardBody, Button, FormGroup, Label, Input } from "reactstrap";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { PostData } from "../../../services/FetchData";
import { ProductContext } from "../../../store";
import { toast } from "react-toastify";

const DRUPAL_BASE = "ms3";

const DEFAULT_PRODUCT_FIELD_MAP = {
	url: "url",
	image: "image_url",
	title: "product_title",
	description: "body",
	price: "price_number",
	priceCurrency: "price_currency",
	vendor: "brand",
	sku: "sku",
};
const DEFAULT_CONTENT_FIELD_MAP = {
	url: "url",
	image: "image_url",
	title: "title",
	description: "body",
};

function mergeWithDefaults(stored, defaults) {
	if (!stored || typeof stored !== "object") return { ...defaults };
	const out = { ...defaults };
	for (const key of Object.keys(defaults)) {
		if (Object.prototype.hasOwnProperty.call(stored, key)) {
			out[key] = stored[key] != null ? String(stored[key]).trim() : "";
		}
	}
	return out;
}

function normSection(s) {
	return (s || "").toLowerCase().replace(/\s+/g, "_");
}

const SKIP_LABEL = "Skip (don't show this field)";

function buildSelectOptions(fieldNames, currentValue) {
	const names = Array.isArray(fieldNames) ? [...fieldNames] : [];
	const hasCurrent = currentValue && String(currentValue).trim() && !names.includes(String(currentValue).trim());
	const options = [{ value: "", label: SKIP_LABEL }];
	names.forEach((n) => options.push({ value: n, label: n }));
	if (hasCurrent) options.push({ value: String(currentValue).trim(), label: `${currentValue} (saved)` });
	return options;
}

const PRODUCT_KEYS = [
	{ key: "url", label: "URL field" },
	{ key: "image", label: "Image field" },
	{ key: "title", label: "Title field" },
	{ key: "description", label: "Description field" },
	{ key: "sku", label: "SKU field" },
	{ key: "price", label: "Price field" },
	{ key: "priceCurrency", label: "Price currency field" },
	{ key: "vendor", label: "Vendor field" },
];
const CONTENT_KEYS = [
	{ key: "url", label: "URL field" },
	{ key: "image", label: "Image field" },
	{ key: "title", label: "Title field" },
	{ key: "description", label: "Description field" },
];

export default function DisplayFieldMapping() {
	const { currentWebsiteId } = useContext(ProductContext);
	const websiteId = currentWebsiteId;
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [autocompleteDisplay, setAutocompleteDisplay] = useState(null);
	const [productFieldMap, setProductFieldMap] = useState({ ...DEFAULT_PRODUCT_FIELD_MAP });
	const [contentFieldMap, setContentFieldMap] = useState({ ...DEFAULT_CONTENT_FIELD_MAP });
	const [productFieldNames, setProductFieldNames] = useState([]);
	const [contentFieldNames, setContentFieldNames] = useState([]);

	useEffect(() => {
		if (!websiteId) return;
		setLoading(true);
		PostData(DRUPAL_BASE, "api/v1/drupal/autocomplete/config", { websiteId })
			.then((res) => {
				const display = res?.autoCompleteConfig?.autocompleteDisplay || {};
				const indexes = res?.autoCompleteConfig?.autocompleteIndexes || [];
				setAutocompleteDisplay(display);
				setProductFieldMap(mergeWithDefaults(display.productFieldMap, DEFAULT_PRODUCT_FIELD_MAP));
				setContentFieldMap(mergeWithDefaults(display.contentFieldMap, DEFAULT_CONTENT_FIELD_MAP));

				const productsSection = indexes.find(
					(s) => normSection(s.section) === "products" || normSection(s.section) === "product"
				);
				const contentSection = indexes.find((s) => normSection(s.section) === "content");
				const productIndexId = productsSection?.index_id ? String(productsSection.index_id).trim() : null;
				const contentIndexId = contentSection?.index_id ? String(contentSection.index_id).trim() : null;

				const promises = [];
				if (productIndexId) {
					promises.push(
						PostData(DRUPAL_BASE, "api/v1/drupal/collection-schema", {
							websiteId,
							index_id: productIndexId,
							index_label: productsSection?.section,
						})
							.then((r) => (r.fields || []).map((f) => f.name))
							.catch(() => [])
					);
				} else {
					promises.push(Promise.resolve([]));
				}
				if (contentIndexId) {
					promises.push(
						PostData(DRUPAL_BASE, "api/v1/drupal/collection-schema", {
							websiteId,
							index_id: contentIndexId,
							index_label: contentSection?.section,
						})
							.then((r) => (r.fields || []).map((f) => f.name))
							.catch(() => [])
					);
				} else {
					promises.push(Promise.resolve([]));
				}
				return Promise.all(promises).then(([productNames, contentNames]) => {
					setProductFieldNames(productNames || []);
					setContentFieldNames(contentNames || []);
				});
			})
			.catch((err) => {
				toast.error(err?.message || "Failed to load config");
				setProductFieldNames([]);
				setContentFieldNames([]);
			})
			.finally(() => setLoading(false));
	}, [websiteId]);

	function setProductField(key, value) {
		setProductFieldMap((prev) => ({ ...prev, [key]: value }));
	}
	function setContentField(key, value) {
		setContentFieldMap((prev) => ({ ...prev, [key]: value }));
	}

	async function handleSave(e) {
		e.preventDefault();
		if (!websiteId) return;
		setSaving(true);
		try {
			await PostData(DRUPAL_BASE, "api/v1/drupal/autocomplete/save", {
				websiteId,
				autocompleteDisplay: {
					...autocompleteDisplay,
					productFieldMap,
					contentFieldMap,
				},
			});
			setAutocompleteDisplay((prev) => ({
				...prev,
				productFieldMap,
				contentFieldMap,
			}));
			toast.success("Saved");
		} catch (err) {
			toast.error(err?.message || "Failed to save");
		} finally {
			setSaving(false);
		}
	}

	if (loading) {
		return (
			<Card>
				<CardBody>
					<h2 className="h5 mb-3"><Skeleton width={220} height={24} /></h2>
					<p className="text-muted small mb-4"><Skeleton count={2} /></p>
					<h3 className="h6 mt-4"><Skeleton width={180} height={20} /></h3>
					{[1, 2, 3, 4, 5, 6, 7].map((i) => (
						<FormGroup key={i}>
							<Skeleton height={20} width={120} className="mb-1" />
							<Skeleton height={38} />
						</FormGroup>
					))}
					<h3 className="h6 mt-4"><Skeleton width={180} height={20} /></h3>
					{[1, 2, 3, 4].map((i) => (
						<FormGroup key={i}>
							<Skeleton height={20} width={120} className="mb-1" />
							<Skeleton height={38} />
						</FormGroup>
					))}
					<div className="mt-4"><Skeleton height={38} width={80} /></div>
				</CardBody>
			</Card>
		);
	}
	if (!websiteId) return <p>Select a website.</p>;

	return (
		<Card>
			<CardBody>
				<form onSubmit={handleSave}>
					<h2 className="h5 mb-3">Display field mapping</h2>
					<p className="text-muted small mb-4">
						Map index field names used for product and content display in autocomplete and on the search results page. Options are loaded from your search index. If a field is not available on your site (e.g. no vendor), choose <strong>Skip</strong> or click the Skip link to hide it.
					</p>

					<h3 className="h6 mt-4">Product display fields</h3>
					<p className="text-muted small">Used for product sections in autocomplete and for the search results page. First option in each dropdown is &quot;Skip&quot; to hide that field.</p>
					{PRODUCT_KEYS.map(({ key, label }) => (
						<FormGroup key={key}>
							<Label>{label}</Label>
							<div className="d-flex align-items-center gap-2 flex-wrap">
								<Input
									type="select"
									className="flex-grow-1"
									style={{ maxWidth: "280px" }}
									value={productFieldMap[key] ?? ""}
									onChange={(e) => setProductField(key, e.target.value)}
								>
									{buildSelectOptions(productFieldNames, productFieldMap[key]).map((opt, idx) => (
										<option key={opt.value ? opt.value : `empty-${key}-${idx}`} value={opt.value}>
											{opt.label}
										</option>
									))}
								</Input>
								{(productFieldMap[key] ?? "").trim() !== "" && (
									<Button
										type="button"
										color="link"
										className="p-0 text-muted small"
										onClick={() => setProductField(key, "")}
									>
										Skip this field
									</Button>
								)}
							</div>
						</FormGroup>
					))}

					<h3 className="h6 mt-4">Content display fields</h3>
					<p className="text-muted small">Used for content sections in autocomplete. First option in each dropdown is &quot;Skip&quot; to hide that field.</p>
					{CONTENT_KEYS.map(({ key, label }) => (
						<FormGroup key={key}>
							<Label>{label}</Label>
							<div className="d-flex align-items-center gap-2 flex-wrap">
								<Input
									type="select"
									className="flex-grow-1"
									style={{ maxWidth: "280px" }}
									value={contentFieldMap[key] ?? ""}
									onChange={(e) => setContentField(key, e.target.value)}
								>
									{buildSelectOptions(contentFieldNames, contentFieldMap[key]).map((opt, idx) => (
										<option key={opt.value ? opt.value : `empty-${key}-${idx}`} value={opt.value}>
											{opt.label}
										</option>
									))}
								</Input>
								{(contentFieldMap[key] ?? "").trim() !== "" && (
									<Button
										type="button"
										color="link"
										className="p-0 text-muted small"
										onClick={() => setContentField(key, "")}
									>
										Skip this field
									</Button>
								)}
							</div>
						</FormGroup>
					))}

					<Button type="submit" color="primary" className="mt-4" disabled={saving}>
						{saving ? "Saving..." : "Save"}
					</Button>
				</form>
			</CardBody>
		</Card>
	);
}
